import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Check, Search } from 'lucide-react';

import { cn } from '@/lib/utils';

export interface SelectOption {
  value: string;
  /** Visible label — accepts rich content (e.g. a status dot + text). */
  label: ReactNode;
  /** Optional leading icon rendered before the label. */
  icon?: ReactNode;
  /** Native title attribute for the option row. */
  title?: string;
  disabled?: boolean;
}

export interface SelectProps {
  options: SelectOption[];
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  /** Visible field label rendered above the control. */
  label?: string;
  /** Error message rendered below the control. */
  error?: string;
  className?: string;
  id?: string;
  /** Accessible name for the trigger (when no visible `label` is used). */
  'aria-label'?: string;
  /** Id(s) of element(s) describing the control (e.g. a hint explaining why it is disabled). */
  'aria-describedby'?: string;
  /** Render an inline search box inside the dropdown. */
  searchable?: boolean;
  searchPlaceholder?: string;
}

/**
 * Design-system single-select control.
 *
 * Declarative `options`/`value`/`onChange` API (replaces the old Radix compound pattern).
 * Options may carry an `icon` and rich `label` content.
 */
export function Select({
  className,
  label,
  error,
  options,
  placeholder,
  value,
  onChange,
  disabled,
  required,
  id,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  searchable,
  searchPlaceholder,
}: SelectProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const selectId = id || label?.toLowerCase().replace(/\s+/g, '-');

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  useEffect(() => {
    if (!searchable) return;
    if (open) searchRef.current?.focus();
    else setQuery('');
  }, [open, searchable]);

  const filteredOptions = useMemo(() => {
    if (!searchable || !query.trim()) return options;
    const q = query.trim().toLowerCase();
    return options.filter(
      (o) => typeof o.label === 'string' && o.label.toLowerCase().includes(q),
    );
  }, [options, query, searchable]);

  const selected = options.find((o) => o.value === value);

  const handleSelect = (next: string): void => {
    onChange?.(next);
    setOpen(false);
  };

  return (
    <div className={cn('flex flex-col gap-1.5', className)} ref={ref}>
      {label && (
        <label htmlFor={selectId} className="text-sm font-medium text-text-primary">
          {label}
          {required && (
            <span className="ml-0.5 text-error" aria-hidden="true">
              *
            </span>
          )}
        </label>
      )}
      <div className="relative">
        <button
          type="button"
          id={selectId}
          disabled={disabled}
          aria-label={ariaLabel}
          aria-describedby={ariaDescribedBy}
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          title={selected?.title}
          className={cn(
            'flex h-[42px] w-full items-center justify-between rounded-[var(--radius-input)] border bg-white px-3.5 py-2.5 text-left text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:cursor-not-allowed disabled:opacity-60',
            error ? 'border-error' : 'border-outlines',
          )}
        >
          <span
            className={cn(
              'flex min-w-0 items-center gap-2 truncate',
              selected ? 'text-text-primary' : 'text-text-placeholder',
            )}
          >
            {selected?.icon}
            <span className="truncate">{selected?.label ?? placeholder ?? 'Select'}</span>
          </span>
          <ChevronDown
            size={16}
            className={cn('shrink-0 text-text-secondary transition-transform', open && 'rotate-180')}
          />
        </button>

        {open && (
          <div
            role="listbox"
            className="absolute left-0 right-0 top-full z-20 mt-1 flex flex-col overflow-hidden rounded-[var(--radius-input)] border border-outlines bg-white shadow-lg"
          >
            {searchable && (
              <div className="relative shrink-0 border-b border-outlines">
                <Search
                  size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary"
                />
                <input
                  ref={searchRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={searchPlaceholder ?? 'Search'}
                  className="w-full bg-transparent py-2 pl-9 pr-3 text-sm placeholder:text-text-placeholder focus:outline-none"
                />
              </div>
            )}
            <div className={cn('flex-1 overflow-auto py-1', searchable ? 'max-h-56' : 'max-h-40')}>
              {placeholder && !query && (
                <button
                  type="button"
                  onClick={() => handleSelect('')}
                  className="flex w-full items-center px-3 py-1.5 text-left text-sm text-text-placeholder hover:bg-subtle-bg"
                >
                  {placeholder}
                </button>
              )}
              {filteredOptions.length === 0 && searchable && query ? (
                <p className="px-3 py-2 text-sm text-text-secondary">No matches for &quot;{query}&quot;.</p>
              ) : (
                filteredOptions.map((o) => {
                  const isSelected = o.value === value;
                  return (
                    <button
                      key={o.value}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      disabled={o.disabled}
                      onClick={() => handleSelect(o.value)}
                      title={o.title}
                      className={cn(
                        'flex w-full items-center justify-between gap-2.5 px-3 py-1.5 text-left text-sm hover:bg-subtle-bg disabled:cursor-not-allowed disabled:opacity-50',
                        isSelected ? 'text-primary' : 'text-text-primary',
                      )}
                    >
                      <span className="flex min-w-0 items-center gap-2 truncate">
                        {o.icon}
                        <span className="truncate">{o.label}</span>
                      </span>
                      {isSelected && (
                        <Check size={14} strokeWidth={3} className="shrink-0 text-primary" />
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
      {error && (
        <p className="text-xs text-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
